import { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { ArrowLeft, User } from "lucide-react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingModal } from "@/components/Onboarding/OnboardingModal";
import { useOnboarding } from "@/hooks/use-onboarding";
import CosmicBackground from "@/components/CosmicBackground";
import { motion, AnimatePresence } from "framer-motion";

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" className="w-5 h-5">
    <path fill="#EA4335" d="M5.26 11c0-.74.2-1.44.58-2.03l-3.2-2.46A10.9 10.9 0 0 0 .54 11c0 1.62.35 3.17 1 4.55l3.24-2.5c-.38-.63-.53-1.33-.53-2.05z"/>
    <path fill="#FBBC05" d="M12 4.75c1.66 0 3.14.58 4.3 1.54l2.75-2.75A10.9 10.9 0 0 0 12 .54 10.96 10.96 0 0 0 2.56 6.5l3.2 2.46a6.5 6.5 0 0 1 6.24-4.2z"/>
    <path fill="#4285F4" d="M12 19.25a6.5 6.5 0 0 1-6.24-4.2L2.56 17.5A10.96 10.96 0 0 0 12 23.46c2.92 0 5.37-.96 7.15-2.6l-3.06-2.35c-.84.57-1.93.91-3.45 1.04a6.5 6.5 0 0 1-5.64-4.15l-3.24 2.5a10.93 10.93 0 0 0 8.24 5.84v-4.5z"/>
    <path fill="#34A853" d="M23.46 12.2c0-.66-.06-1.3-.18-1.92H12v3.67h6.46a5.53 5.53 0 0 1-2.4 3.63l3.05 2.35c1.8-1.65 2.83-4.1 2.83-7.73z"/>
  </svg>
);

const Auth = () => {
  const { user, loading, signIn, signUp, signInWithSocialProvider } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  const [formState, setFormState] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    username: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailConfirmationRequired, setEmailConfirmationRequired] = useState(false);
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const { showOnboarding, completeOnboarding } = useOnboarding();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'signup') {
      setActiveTab('signup');
    } else {
      setActiveTab('signin');
    }

    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    
    if (errorParam && errorDescription) {
      setAuthError(`${errorParam}: ${errorDescription}`);
      toast.error(errorDescription);
    }
  }, [searchParams]);

  if (user && !loading) {
    return <Navigate to="/" />;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (authError) setAuthError(null);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    
    if (!formState.email || !formState.password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    try {
      setIsSubmitting(true);
      await signIn(formState.email, formState.password);
      navigate("/");
    } catch (error: any) {
      console.error("Sign in error:", error);
      setAuthError(error.message || "Failed to sign in");
      
      if (error.message && error.message.includes("Email not confirmed")) {
        setEmailConfirmationRequired(true);
        setConfirmedEmail(formState.email);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    
    if (!formState.email || !formState.password || !formState.username) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formState.password !== formState.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formState.username.length < 3) {
      toast.error("Username must be at least 3 characters long");
      return;
    }
    
    try {
      setIsSubmitting(true);
      await signUp(formState.email, formState.password, formState.username);
      
      setEmailConfirmationRequired(true);
      setConfirmedEmail(formState.email);
    } catch (error: any) {
      console.error("Sign up error:", error);
      setAuthError(error.message || "Failed to sign up");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setAuthError(null);
      await signInWithSocialProvider("google");
    } catch (error: any) {
      console.error("Google sign in error:", error);
      setAuthError(error.message || "Failed to sign in with Google");
    }
  };

  const resendConfirmationEmail = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: confirmedEmail,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success("Confirmation email resent. Please check your inbox.");
    } catch (error: any) {
      console.error("Error sending confirmation email:", error);
      toast.error("Could not resend confirmation email. Please try again later.");
    }
  };

  const handleContinueAsGuest = () => {
    toast.success("Continuing as guest");
    navigate("/");
  };

  return (
    <>
      <OnboardingModal isOpen={showOnboarding} onClose={completeOnboarding} />
      <CosmicBackground />
      
      <div className="min-h-screen text-white overflow-hidden relative flex flex-col">
        {/* Compact Header */}
        <div className="relative z-10 flex items-center justify-between px-4 py-4 md:px-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-white/80 hover:text-white hover:bg-white/10 transition-all"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-brand-full" />
            <h1 className="text-lg font-display font-bold">
              <span className="text-gradient-hero">ThePromptSpace</span>
            </h1>
          </div>
          
          <div className="w-10" /> {/* Spacer for centering */}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-6 md:py-8">
          <div className="w-full max-w-md space-y-4">
            {/* Alert Messages */}
            {emailConfirmationRequired && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-4 border-[#00C2FF]/30"
              >
                <Alert className="bg-transparent border-0">
                  <InfoIcon className="h-4 w-4 text-[#00C2FF]" />
                  <AlertDescription className="text-white/90 text-sm">
                    <p className="font-medium mb-2">Check your email to confirm your account</p>
                    <p className="text-xs text-white/70 mb-2">
                      We've sent a confirmation email to <strong>{confirmedEmail}</strong>
                    </p>
                    <button 
                      className="text-[#00C2FF] text-xs underline cursor-pointer"
                      onClick={resendConfirmationEmail}
                    >
                      Resend confirmation email
                    </button>
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
            
            {authError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-4 border-[#FF00A8]/30"
              >
                <Alert className="bg-transparent border-0">
                  <InfoIcon className="h-4 w-4 text-[#FF00A8]" />
                  <AlertDescription className="text-white/90 text-sm">
                    {authError}
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}

            {/* Auth Card */}
            <Card className="bg-black/60 backdrop-blur-lg border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
              <CardContent className="p-6 md:p-8 space-y-6">
                {/* Title & Subtitle */}
                <div className="text-center space-y-2">
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-gradient-hero">
                    {activeTab === "signin" ? "Sign In" : "Create an Account"}
                  </h2>
                  <p className="text-sm text-white/60">
                    {activeTab === "signin" 
                      ? "Access your prompts and community" 
                      : "Join creators building the future of GenAI"}
                  </p>
                </div>

                {/* Segment Toggle */}
                <div className="relative bg-white/5 rounded-xl p-1 grid grid-cols-2 gap-1">
                  <motion.div
                    className="absolute inset-y-1 w-[calc(50%-4px)] bg-gradient-brand-full rounded-lg shadow-lg"
                    animate={{
                      x: activeTab === "signin" ? 4 : "calc(100% + 4px)"
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                  <button
                    onClick={() => setActiveTab("signin")}
                    className={`relative z-10 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === "signin" ? "text-white" : "text-white/60 hover:text-white/80"
                    }`}
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setActiveTab("signup")}
                    className={`relative z-10 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === "signup" ? "text-white" : "text-white/60 hover:text-white/80"
                    }`}
                  >
                    Sign Up
                  </button>
                </div>

                {/* Social Login */}
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full bg-white/5 hover:bg-white/10 border-white/10 text-white transition-all hover:scale-[1.02]"
                  onClick={handleGoogleSignIn}
                >
                  <GoogleIcon />
                  <span className="ml-2">Continue with Google</span>
                </Button>

                {/* Divider */}
                <div className="relative">
                  <Separator className="bg-white/10" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-black/60 px-3 text-xs text-white/40 uppercase tracking-wider">
                      or continue with email
                    </span>
                  </div>
                </div>

                {/* Forms with Animation */}
                <AnimatePresence mode="wait">
                  {activeTab === "signin" ? (
                    <motion.form
                      key="signin"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                      onSubmit={handleSignIn}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="signin-email" className="text-white/80 text-sm">Email</Label>
                        <Input
                          id="signin-email"
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          value={formState.email}
                          onChange={handleInputChange}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00C2FF] focus:ring-[#00C2FF]/30 transition-all"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label htmlFor="signin-password" className="text-white/80 text-sm">Password</Label>
                          <a href="#" className="text-xs text-[#00C2FF] hover:text-[#00C2FF]/80 transition-colors">
                            Forgot password?
                          </a>
                        </div>
                        <Input
                          id="signin-password"
                          name="password"
                          type="password"
                          value={formState.password}
                          onChange={handleInputChange}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00C2FF] focus:ring-[#00C2FF]/30 transition-all"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-gradient-brand-full hover:opacity-90 text-white font-medium shadow-lg hover:shadow-[0_0_30px_rgba(0,194,255,0.3)] transition-all hover:scale-[1.02]"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Signing in...
                          </span>
                        ) : (
                          "Sign In"
                        )}
                      </Button>
                    </motion.form>
                  ) : (
                    <motion.form
                      key="signup"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      onSubmit={handleSignUp}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="signup-email" className="text-white/80 text-sm">Email</Label>
                        <Input
                          id="signup-email"
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          value={formState.email}
                          onChange={handleInputChange}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00C2FF] focus:ring-[#00C2FF]/30 transition-all"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="username" className="text-white/80 text-sm">Username</Label>
                        <Input
                          id="username"
                          name="username"
                          type="text"
                          placeholder="coolpromptuser"
                          value={formState.username}
                          onChange={handleInputChange}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00C2FF] focus:ring-[#00C2FF]/30 transition-all"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="signup-password" className="text-white/80 text-sm">Password</Label>
                        <Input
                          id="signup-password"
                          name="password"
                          type="password"
                          value={formState.password}
                          onChange={handleInputChange}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00C2FF] focus:ring-[#00C2FF]/30 transition-all"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password" className="text-white/80 text-sm">Confirm Password</Label>
                        <Input
                          id="confirm-password"
                          name="confirmPassword"
                          type="password"
                          value={formState.confirmPassword}
                          onChange={handleInputChange}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00C2FF] focus:ring-[#00C2FF]/30 transition-all"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-gradient-brand-full hover:opacity-90 text-white font-medium shadow-lg hover:shadow-[0_0_30px_rgba(0,194,255,0.3)] transition-all hover:scale-[1.02]"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Creating account...
                          </span>
                        ) : (
                          "Create Account"
                        )}
                      </Button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </CardContent>

              <CardFooter className="flex flex-col gap-4 p-6 md:p-8 pt-0">
                {/* Continue as Guest */}
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full bg-transparent hover:bg-white/5 border-white/20 text-white/70 hover:text-white transition-all"
                  onClick={handleContinueAsGuest}
                >
                  <User className="mr-2 h-4 w-4" />
                  Continue as Guest
                </Button>

                {/* Toggle Link */}
                <div className="text-center text-sm text-white/50">
                  {activeTab === "signin" ? (
                    <>
                      Don't have an account?{" "}
                      <button
                        type="button"
                        className="text-[#00C2FF] hover:text-[#00C2FF]/80 font-medium transition-colors"
                        onClick={() => setActiveTab("signup")}
                      >
                        Sign up
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{" "}
                      <button
                        type="button"
                        className="text-[#00C2FF] hover:text-[#00C2FF]/80 font-medium transition-colors"
                        onClick={() => setActiveTab("signin")}
                      >
                        Sign in
                      </button>
                    </>
                  )}
                </div>

                {/* Footer Legal */}
                <div className="text-center text-xs text-white/30 pt-2">
                  By continuing, you agree to our{" "}
                  <a href="/terms" className="underline hover:text-white/50 transition-colors">Terms</a>
                  {" "}and{" "}
                  <a href="/privacy" className="underline hover:text-white/50 transition-colors">Privacy Policy</a>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Auth;
